// Third-party
import moment from 'moment';
import get from 'lodash/get';

// Utils
import { DISCLAIMER_KEYS, DISCLAIMER_TAG } from './constants';
import { formatMessageWithLocale, formatLocalizedString } from '../i18n';
import { PERIOD_MONTHS, TIME_PERIODS, DISCLAIMER_RELEVANCE_YEARS } from '../constants';
import { calculateDateRangeFromPeriod } from '../time-range-presets';
import { getEarliestCommonInceptionDate } from '../comparison/items';
import helpers from './helpers';

// Types
import Dictionary from '@/types/dictionary';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import ComparisonHolding from '@/types/comparison-holding';
import RollingIntervals from '@/types/rolling-intervals';
import ShortHistoryDisclaimer from '@/types/short-history-disclaimer';
import CustomDisclaimerHolding from '@/types/custom-disclaimer-holding';
import Comparison from '@/types/comparison';
import ShortHistoryDisclaimerHolding from '@/types/short-history-disclaimer-holding';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import Disclaimer from '@/types/disclaimer';
import Nullable from '@/types/nullable';

/**
 * Utility function to get a date that is a specified number of years ago from today
 * @param years Number of years to subtract from today
 * @returns Date object representing the calculated date
 */
export const getDateYearsAgo = (years: number): Date => moment().subtract(years, 'years').toDate();

/**
 * Utility function to find the field with the most holdings that matches the filter
 * @param fields Array of short history disclaimer fields
 * @param fieldsFilter Optional filter for specific fields
 * @returns The field with the most holdings that matches the filter, or null if none found
 */
export const findFieldWithMostHoldings = (fields: ShortHistoryDisclaimer[], fieldsFilter?: string): ShortHistoryDisclaimer | null =>
  fields.reduce<ShortHistoryDisclaimer | null>((maxField, currentField) => {
    // Skip field if it doesn't match the filter
    if (fieldsFilter && !currentField.field.includes(fieldsFilter)) {
      return maxField;
    }

    // Return current field if it has more holdings than the current max
    if (!maxField || currentField.holdings.length > maxField.holdings.length) {
      return currentField;
    }

    return maxField;
  }, null);

/**
 * Utility function to convert dateQuickRange to actual start/end dates
 * Uses the same logic as chart-container-helpers for consistency
 * @param dateQuickRange The time period to convert
 * @param startDate Explicit start date (takes priority if provided)
 * @param endDate Explicit end date (takes priority if provided)
 * @param items Optional comparison items for calculating common inception date (for CI/INC periods)
 * @param useMonthly Optional flag for monthly vs weekly data (for inception calculations)
 * @returns Object with startDate and endDate
 */
export const getDateRangeFromQuickRange = (
  dateQuickRange?: Nullable<TIME_PERIODS>,
  startDate?: Nullable<Date>,
  endDate?: Date,
  items?: ComparisonItem[],
  useMonthly?: boolean,
  applyRelevanceYearLimit: boolean = false,
): { startDate?: Date; endDate?: Date } => {
  if (startDate || endDate) {
    return { startDate: startDate || undefined, endDate };
  }

  if (!dateQuickRange && applyRelevanceYearLimit) {
    const relevanceLimitDate = getDateYearsAgo(DISCLAIMER_RELEVANCE_YEARS);
    return { startDate: relevanceLimitDate, endDate: endDate || moment().toDate() };
  }

  const result = dateQuickRange
    ? calculateDateRangeFromPeriod(dateQuickRange, {
        referenceEndDate: endDate || moment().toDate(),
        items,
        useMonthly: useMonthly ?? false,
        getEarliestCommonInceptionDate,
        applyRelevanceYearLimit,
      })
    : { startDate: undefined, endDate: undefined };

  return {
    startDate: result.startDate || undefined,
    endDate: result.endDate,
  };
};

/**
 * Generate the allocations disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Allocations dates.
 */
export const getAllocationsAsAtDates = (items: ComparisonItem[], locale?: Locale) => {
  const getHoldingDate = (holding: ComparisonHolding, item: ComparisonItem) =>
    item.dates.allocations || (item.dates.portfolioAllocations && item.dates.portfolioAllocations[holding.id]) || undefined;

  return items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: item.dates.allocations ? [item.dates.allocations] : (item.holdings.map(h => getHoldingDate(h, item)).filter(d => d !== undefined) as Date[]),
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));
};

type DetailedDisclaimerDate = {
  date: Date;
  symbol?: string;
};

/**
 * Generate the detailed allocations disclaimer dates for comparison items
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Detailed allocations dates.
 */
export const getDetailedAllocationsAsAtDates = (items: ComparisonItem[], locale?: Locale) => {
  const getHoldingDate = (holding: ComparisonHolding, item: ComparisonItem) => {
    if (item.dates.allocations) {
      return { date: item.dates.allocations };
    }

    const { portfolioAllocations } = item.dates;
    if (portfolioAllocations && portfolioAllocations[holding.id]) {
      return {
        date: portfolioAllocations ? portfolioAllocations[holding.id] : null,
        symbol: holding.symbol,
      };
    }

    return undefined;
  };

  return items.map((item: ComparisonItem) => {
    let dates;
    if (item.dates.allocations) {
      dates = [{ date: item.dates.allocations }];
    } else {
      dates = item.holdings.map(h => getHoldingDate(h, item)).filter(d => d !== undefined) as DetailedDisclaimerDate[];
    }

    return {
      id: item._id,
      name: formatLocalizedString(item.name, locale),
      dates,
      color: item.color,
      type: item.type,
      holdings: item.holdings,
    };
  });
};

/**
 * Generate the holdings disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Holdings dates.
 */
export const getHoldingsAsAtDates = (items: ComparisonItem[], locale?: Locale) => {
  const getHoldingDate = (holding: ComparisonHolding, item: ComparisonItem) =>
    item.dates.holdings || (item.dates.portfolioHoldings && item.dates.portfolioHoldings[holding.id]) || undefined;

  return items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: item.dates.allocations ? [item.dates.allocations] : (item.holdings.map(h => getHoldingDate(h, item)).filter(d => d) as Date[]),
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));
};

/**
 * Generate the holdings disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Holdings dates.
 */
export const getDetailedHoldingsAsAtDates = (items: ComparisonItem[], locale?: Locale) => {
  const getHoldingDate = (holding: ComparisonHolding, item: ComparisonItem) => {
    if (item.dates.holdings) {
      return { date: item.dates.holdings };
    }

    const { portfolioHoldings } = item.dates;
    if (portfolioHoldings && portfolioHoldings[holding.id]) {
      return {
        date: portfolioHoldings ? portfolioHoldings[holding.id] : null,
        symbol: holding.symbol,
      };
    }

    return undefined;
  };

  return items.map((item: ComparisonItem) => {
    let dates;
    if (item.dates.allocations) {
      dates = [{ date: item.dates.allocations }];
    } else {
      dates = item.holdings.map(h => getHoldingDate(h, item)).filter(d => d !== undefined) as DetailedDisclaimerDate[];
    }

    return {
      id: item._id,
      name: formatLocalizedString(item.name, locale),
      dates,
      color: item.color,
      type: item.type,
      holdings: item.holdings,
    };
  });
};

/**
 * Processes an array of ComparisonItems, and generates investment information including investment info disclaimer dates for each item.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns array of objects, where each object represents a comparison item with specific information, including investments dates info.
 * The dates array includes either the allocations date or an array of dates extracted from holdings.
 */
export const getInvestmentInfoDates = (items: ComparisonItem[], locale?: Locale) => {
  const getInvestmentInfoDate = (holding: ComparisonHolding, item: ComparisonItem) =>
    item.dates.investmentInfo || (item.dates.portfolioInvestmentInfo && item.dates.portfolioInvestmentInfo[holding.id]) || undefined;

  return items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: item.dates.allocations ? [item.dates.allocations] : (item.holdings.map(h => getInvestmentInfoDate(h, item)).filter(d => d) as Date[]),
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));
};

/**
 * Generate the fixed income sector disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Fixed income sector dates.
 */
export const getFixedIncomeSectorAsAtDates = (items: ComparisonItem[], locale?: Locale) => {
  const getFixedIncomeSectorDate = (holding: ComparisonHolding, item: ComparisonItem) =>
    item.dates.fixedIncomeSector || (item.dates.portfolioFixedIncomeSector && item.dates.portfolioFixedIncomeSector[holding.id]) || undefined;

  return items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: item.dates.allocations ? [item.dates.allocations] : (item.holdings.map(h => getFixedIncomeSectorDate(h, item)).filter(d => d) as Date[]),
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));
};

/**
 * Generate the risk disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Risk dates.
 */
export const getRiskDataAsAtDates = (items: ComparisonItem[], locale?: Locale) =>
  items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: item.dates.risk && item.dates.risk.endDate ? [item.dates.risk.endDate] : [],
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));

/**
 * Generate the risk disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Risk dates.
 */
export const getUpDownMonthsAsAtDates = (items: ComparisonItem[], locale?: Locale) =>
  items.map((item: ComparisonItem) => {
    const lastTimestamp =
      item.performance.rolling && item.performance.rolling.monthly.length > 0
        ? item.performance.rolling.monthly[item.performance.rolling.monthly.length - 1][0]
        : undefined;
    return {
      id: item._id,
      name: formatLocalizedString(item.name, locale),
      dates: lastTimestamp ? [new Date(lastTimestamp)] : [],
      color: item.color,
      type: item.type,
      holdings: item.holdings,
    };
  });

/**
 * Generate the performance disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Performance dates.
 */
export const getPerformanceAsAtDates = (items: ComparisonItem[], locale?: Locale) => {
  const getHoldingDate = (holding: ComparisonHolding, item: ComparisonItem) =>
    item.dates.growth || (item.dates.portfolioGrowth && item.dates.portfolioGrowth[holding.id]) || undefined;

  return items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: item.dates.growth ? [item.dates.growth] : (item.holdings.map(h => getHoldingDate(h, item)).filter(d => d) as Date[]),
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));
};

/**
 * Generate the performance disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Performance dates and symbol or name(if symbol does not exist) for the instrument.
 */
export const getDetailedPerformanceAsAtDates = (items: ComparisonItem[], locale?: Locale) => {
  const getHoldingDate = (holding: ComparisonHolding, item: ComparisonItem) => {
    if (item.dates.growth) {
      return { date: item.dates.growth };
    }

    const { portfolioGrowth } = item.dates;
    if (portfolioGrowth && portfolioGrowth[holding.id]) {
      return {
        date: portfolioGrowth ? portfolioGrowth[holding.id] : null,
        symbol: holding.symbol ? holding.symbol : formatLocalizedString(holding.name, locale),
      };
    }

    return undefined;
  };

  return items.map((item: ComparisonItem) => {
    let dates;
    if (item.dates.allocations) {
      dates = [{ date: item.dates.allocations }];
    } else {
      dates = item.holdings.map(h => getHoldingDate(h, item)).filter(d => d !== undefined) as DetailedDisclaimerDate[];
    }

    return {
      id: item._id,
      name: formatLocalizedString(item.name, locale),
      dates,
      color: item.color,
      type: item.type,
      holdings: item.holdings,
    };
  });
};

/**
 * Generate the Lipper Leaders disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Lipper Leaders dates.
 */
export const getLipperAsAtDates = (items: ComparisonItem[], locale?: Locale) =>
  items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: item.dates.lipperScore ? [item.dates.lipperScore] : [],
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));

/**
 * Generate the rolling data disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Rolling disclaimer dates.
 */
export const getRollingDisclaimerDates = (items: ComparisonItem[], interval: RollingIntervals, endDate?: Date, locale?: Locale) => {
  const getHoldingDate = (holding: ComparisonHolding) => {
    if (!holding.rolling || !holding.rolling[interval].length) {
      return undefined;
    }

    let trimmedRolling = holding.rolling[interval];
    if (endDate) {
      trimmedRolling = holding.rolling[interval].filter(rolling => rolling[0] <= endDate.getTime());
    }

    if (!trimmedRolling.length) {
      return undefined;
    }
    const lastDate = new Date(trimmedRolling[trimmedRolling.length - 1][0]);
    return !endDate ? lastDate : new Date(Math.min(lastDate.getTime(), endDate.getTime()));
  };

  return items.map((item: ComparisonItem) => {
    const earliestDate = (item.holdings.map(getHoldingDate).filter(d => d) as Date[]).sort((a: Date, b: Date) => a.getTime() - b.getTime())[0];

    return {
      id: item._id,
      name: formatLocalizedString(item.name, locale),
      dates: earliestDate ? [earliestDate] : [],
      color: item.color,
      type: item.type,
      holdings: item.holdings,
    };
  });
};

export const getShortHistoryDatesForItem = (item: ComparisonItem, locale?: Locale, fieldsFilter?: string) => {
  const disclaimer = item.disclaimers && item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS];

  if (!disclaimer || !disclaimer.data) {
    return [];
  }

  const fields = disclaimer.data.fields as ShortHistoryDisclaimer[];
  const field = findFieldWithMostHoldings(fields, fieldsFilter);

  if (!field) {
    return [];
  }

  return field.holdings.map(h =>
    formatMessageWithLocale('Comparison.LongHistoryTooltip.PriorTo', locale, {
      symbol: h.symbol || h.name || '',
      date: moment(h.date).format(formatMessageWithLocale('Common.DateFormats.Short', locale)),
    }),
  );
};

/**
 * Get history disclaimer dates for an item with timeframe filtering
 * This is a specialized version for components that need to filter based on selected timeframes
 * @param item ComparisonItem to generate disclaimer dates for
 * @param locale Locale for formatting
 * @param fieldsFilter Optional filter for specific fields
 * @param startDate Explicit start date for filtering disclaimers
 * @param endDate Explicit end date for filtering disclaimers
 * @returns Array of formatted disclaimer messages for holdings based on selected timeframe with incomplete data
 */
export const getShortHistoryDatesForItemWithTimeframe = (
  item: ComparisonItem,
  locale?: Locale,
  fieldsFilter?: string,
  startDate?: Nullable<Date>,
  endDate?: Nullable<Date>,
) => {
  const disclaimer = item.disclaimers && item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS];

  if (!disclaimer || !disclaimer.data) {
    return [];
  }

  const fields = disclaimer.data.fields as ShortHistoryDisclaimer[];
  const field = findFieldWithMostHoldings(fields, fieldsFilter);

  if (!field) {
    return [];
  }

  const relevanceLimitDate = getDateYearsAgo(DISCLAIMER_RELEVANCE_YEARS);

  const filteredHoldings = field.holdings.filter(holding => {
    const holdingDate = holding.date;

    // Apply relevance limit only when no startDate is provided
    if (!startDate && !moment(holdingDate).isAfter(relevanceLimitDate)) {
      return false;
    }

    // Check start date constraint if provided
    if (startDate && !moment(holdingDate).isAfter(startDate)) {
      return false;
    }

    // Check end date constraint if provided
    if (endDate && !moment(holdingDate).isBefore(endDate)) {
      return false;
    }

    return true;
  });

  return filteredHoldings.map((h: any) =>
    formatMessageWithLocale('Comparison.LongHistoryTooltip.PriorTo', locale, {
      symbol: h.symbol || h.name || '',
      date: moment(h.date).format(formatMessageWithLocale('Common.DateFormats.Short', locale)),
    }),
  );
};

export const getCustomDisclaimersForItem = (item: ComparisonItem, tags?: DISCLAIMER_TAG[]): CustomDisclaimerHolding[] => {
  const instrumentCustomDisclaimer = item?.disclaimers?.[DISCLAIMER_KEYS.INSTRUMENT_CUSTOM_DISCLAIMER];
  const hasCustomDisclaimer = instrumentCustomDisclaimer;
  const disclaimer = item.disclaimers && hasCustomDisclaimer;

  if (!disclaimer || !disclaimer.data || !disclaimer.data.holdings) {
    return [];
  }

  // Find field with most holdings missing data
  let holdings = disclaimer.data.holdings as CustomDisclaimerHolding[];

  const disclaimerData = holdings.filter(holding => holding.content && Object.values(holding.content).some(value => value !== ''));

  if (!disclaimerData.length) {
    return [];
  }

  if (tags != null) {
    holdings = holdings.filter(h => h.tags?.some(tag => tags.includes(tag as DISCLAIMER_TAG)));
  }

  return holdings.map(h => ({
    name: h.name || '',
    symbol: h.symbol || h.name || '',
    content: h.content,
  }));
};

export const getFieldValueDisclaimer = (comparison: Comparison, item: ComparisonItem, fieldKey: string, locale?: Locale) => {
  const disclaimer = item.disclaimers && item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS];

  if (!comparison.shortHistory || !disclaimer || !disclaimer.data || !disclaimer.data.fields) {
    return null;
  }

  const field = disclaimer.data.fields.find((f: any) => f.field === fieldKey);

  if (!field) {
    return null;
  }

  return {
    title: formatMessageWithLocale('Comparison.Table.LongHistoryTooltip.Title', locale),
    message: formatMessageWithLocale('Comparison.Table.LongHistoryTooltip.Message', locale),
    list: field.holdings.map((h: ShortHistoryDisclaimerHolding) =>
      formatMessageWithLocale('Comparison.Table.LongHistoryTooltip.PriorTo', locale, {
        symbol: h.symbol || h.name || '',
        date: moment(h.date).format(formatMessageWithLocale('Common.DateFormats.Short', locale)),
      }),
    ),
  };
};

export const getComparisonBenchmarkYoungestHolding = (item: ComparisonItem) => {
  let youngestHolding = null;

  for (const holding of item.holdings) {
    if (!holding.rolling) {
      return null;
    }

    const monthlyRolling = holding.rolling.monthly;

    if (!monthlyRolling || !monthlyRolling.length) {
      return null;
    }

    if (!youngestHolding || monthlyRolling[0][0] > youngestHolding.date) {
      youngestHolding = {
        symbol: holding.symbol,
        date: monthlyRolling[0][0],
      };
    }
  }

  return youngestHolding;
};

export const getComparisonItemBenchmarkDisclaimers = (item: ComparisonItem, data: ComparisonBenchmark[]) => {
  const disclaimers: Dictionary<Disclaimer> = {};
  const youngestHolding = getComparisonBenchmarkYoungestHolding(item);

  if (youngestHolding) {
    disclaimers[DISCLAIMER_KEYS.BENCHMARK_NO_DATA_BEFORE_DATE] = {
      key: DISCLAIMER_KEYS.BENCHMARK_NO_DATA_BEFORE_DATE,
      data: {
        date: youngestHolding.date,
        symbol: youngestHolding.symbol,
      },
    };
  }

  const holdingsWithNoData = item.holdings.filter(holding => !holding.rolling || holding.rolling.monthly.length < PERIOD_MONTHS['1Y']);

  // Find all holdings with no monthly rolling data.
  if (holdingsWithNoData.length > 0) {
    const list = helpers.formatComparisonItemHoldingsList(holdingsWithNoData);

    disclaimers[DISCLAIMER_KEYS.PORTFOLIO_NO_DATA] = {
      key: DISCLAIMER_KEYS.PORTFOLIO_NO_DATA,
      data: {
        list,
      },
    };

    disclaimers[DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS] = {
      key: DISCLAIMER_KEYS.PORTFOLIO_NO_RISK_METRICS,
      data: {
        list,
      },
    };
  }

  const shortDisclaimers = helpers.getComparisonBenchmarkShortHistoryDisclaimers(item);

  const benchmarkDisclaimers = helpers.getBenchmarkItemDisclaimers(item, data);
  if (benchmarkDisclaimers.length > 0) {
    disclaimers[DISCLAIMER_KEYS.BENCHMARK_PROVIDERS] = {
      key: DISCLAIMER_KEYS.BENCHMARK_PROVIDERS,
      data: benchmarkDisclaimers,
    };
  }
  if (shortDisclaimers.length > 0) {
    disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS] = {
      key: DISCLAIMER_KEYS.SHORT_HOLDINGS,
      data: {
        fields: shortDisclaimers,
      },
    };
  }

  return disclaimers;
};

/**
 * Generate the characteristics disclaimer dates for comparison items.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Characteristics dates.
 */
export const getCharacteristicsAsAtDates = (items: ComparisonItem[], locale?: Locale) =>
  items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: item.dates.characteristics ? [item.dates.characteristics] : [],
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));

/**
 * Generate custom allocation disclaimer dates for comparison items at a specifed custom allocations path.
 * @param items Comparison items to generate the disclaimer dates for.
 * @returns Dates.
 */
export const getItemDatesFromPath = (items: ComparisonItem[], datesPath: string, locale?: Locale) => {
  const getAsAt = (item: ComparisonItem) => get(item, datesPath) as Nullable<Date>;

  return items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: items.map(item => getAsAt(item)).filter(date => date != null) as Date[],
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));
};

export const getYieldToMaturityAsAtDates = (items: ComparisonItem[], locale?: Locale) => {
  const getYieldToMaturityDate = (holding: ComparisonHolding, item: ComparisonItem) =>
    (item.dates.portfolioYieldToMaturity && item.dates.portfolioYieldToMaturity[holding.id]) || undefined;

  return items.map((item: ComparisonItem) => ({
    id: item._id,
    name: formatLocalizedString(item.name, locale),
    dates: item.dates.yieldToMaturity ? [item.dates.yieldToMaturity] : (item.holdings.map(h => getYieldToMaturityDate(h, item)).filter(d => d) as Date[]),
    color: item.color,
    type: item.type,
    holdings: item.holdings,
  }));
};
